import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { CompanyGovernmentAllocationData } from '../types/companyGovernmentAllocation.type'

type UseCompanyGovernmentAllocationsInfiniteProps<T> = {
  filters: {
    companyId: string
    branchId?: string
    startDate?: string
    endDate?: string
    showDescendants?: boolean;
    status?: string;
  }
  mapper?: (data: any) => T
}

export const useCompanyGovernmentAllocationsInfinite = <T,>({
  filters: { companyId, branchId, startDate, endDate, showDescendants, status },
  mapper = Mapper.default<T>(),
}: UseCompanyGovernmentAllocationsInfiniteProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useInfiniteQuery({
    queryKey: ['companyGovernmentAllocations', companyId, branchId, startDate, endDate, showDescendants, status],
    enabled: !!companyId,
    queryFn: ({ pageParam }) => {
      return privateAxios.get(`/billing/government_allocations`, {
        params: {
          'q[company_id_eq]': companyId,
          'q[branch_id_eq]': branchId,
          'q[applicable_start_date_lteq]': startDate,
          'q[applicable_end_date_gteq]': endDate,
          'q[show_from_descendants]': showDescendants,
          'q[status_eq]': status,
          page: pageParam
        },
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[]; meta: MetaResponse } }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    select: (data) => {
      const flatData = data?.pages?.reduce((acc, page) => {
        return [...acc, ...page?.data?.data]
      }, [] as CompanyGovernmentAllocationData[])

      return {
        data: mapper ? mapper(flatData) : (flatData as T),
        meta: data?.pages[data?.pages?.length - 1]?.data?.meta as MetaResponse,
      }
    },
    placeholderData: keepPreviousData,
  })

  return { data, meta, ...rest }
}
